import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { BaseModal } from '@src/common/components/BaseModal';
import { Divider } from '@src/common/components/Divider';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { AddNoteForm } from '@src/notes/AddNoteForm';
import { AddNoteSchema } from '@src/notes/AddNoteSchema';
import { NoteParentType } from '@src/notes/domain/Note';
import { NoteContext } from '@src/notes/domain/NoteContext';
import { NoteAssembler } from '@src/notes/NoteAssembler';
import { NoteContextField } from '@src/notes/NoteFields';
import { ModelPermitDescription } from '@src/orders/components/modal/ModalPermitDescription';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import { useCancelPermit } from '@src/orders/hooks/useCancelPermit';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type * as yup from 'yup';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
  orderId: string;
  permit: PermitSummary;
}

export const CancelPermitApplicationModal = ({
  open,
  onClose,
  onSuccess,
  onError,
  orderId,
  permit,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthenticatedUser();

  const { cancelPermit } = useCancelPermit({
    orderId,
    onSuccess,
    onError,
  });

  //oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const form = useForm<yup.InferType<typeof AddNoteSchema> | any>({
    resolver: yupResolver(AddNoteSchema),
    values: {
      [NoteContextField.fieldName]: [NoteContext.cancel.valueOf()],
    },
  });

  const confirm: SubmitHandler<yup.InferType<typeof AddNoteSchema>> = (
    data,
  ) => {
    cancelPermit({
      permitApplicationId: permit.id,
      note: NoteAssembler.fromForm(
        user.id,
        { id: permit.id, type: NoteParentType.PERMIT_APPLICATION },
        data,
      ),
    });
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={t('permitSummaries.cancel')}
    >
      <form>
        <ModelPermitDescription
          jurisdiction={permit.jurisdiction}
          permitType={permit.type}
          stateId={permit.stateId}
        />
        <Stack paddingY={theme.spacing['6']}>
          <Typography variant="body2">
            <Trans
              i18nKey="permitSummaries.cancelMessage"
              components={{ strong: <strong style={{ fontWeight: 600 }} /> }}
            />
          </Typography>
        </Stack>

        <Stack paddingY={theme.spacing['6']}>
          <AddNoteForm
            control={form.control}
            context={NoteContext.cancel}
            setValue={form.setValue}
          />
          <Divider style={{ marginTop: theme.spacing['6'] }} />
        </Stack>
        <CloseConfirmModalFooter
          onClose={onClose}
          confirm={form.handleSubmit(confirm)}
          confirmLabel={t('permitSummaries.cancel')}
        />
      </form>
    </BaseModal>
  );
};
